import React from "react";
import {
  Box,
  Grid,
  Stack,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
} from "@mui/material";
import CustomTypography from "../../globalStyles/fonts";
import { formatDate, formatDateTime } from "../../utils/formatDate";
import {
  arrivedQty,
  receivingBySku,
  receivingTotals,
  lastReceivedDate,
} from "../../utils/poArrivals";

/**
 * "What arrived, and what was received" for a purchase order.
 *
 * Reads `asn.arrivals[]` — one entry per physical delivery, with the warehouse's
 * goods-in bookings nested underneath as `receipts[]`. See utils/poArrivals.js
 * for why arrived and received are tracked as two different numbers; the short
 * version is that a delivery on the dock is arrived and not received, and this
 * view exists to make that gap visible.
 *
 * Every quantity shown is the provider's, not the portal's. `quantity_accepted`
 * (the portal's own stock-in count, entered by a warehouse operator) is
 * deliberately NOT mixed in here — it lives in the SKU Items section above, and
 * the two disagreeing is a real signal an operator needs to be able to see
 * rather than a rendering bug to be smoothed over.
 */

/** Provider arrival statuses -> chip colours. Kept verbatim rather than mapped
 *  onto ASN_STATUSES: a PO has one status, but it can have several arrivals in
 *  different states at once, which is what "partially received" means. */
const ARRIVAL_STATUS_COLORS = {
  received: { bg: "#E8F5E9", color: "#2E7D32" },
  partialreceived: { bg: "#FFF3E0", color: "#E65100" },
  shipped: { bg: "#E3F2FD", color: "#1565C0" },
  expected: { bg: "#E3F2FD", color: "#1565C0" },
  draft: { bg: "#ECEFF1", color: "#546E7A" },
  canceled: { bg: "#FFEBEE", color: "#C62828" },
  failed: { bg: "#FFEBEE", color: "#C62828" },
};

const statusChipSx = (status) =>
  ARRIVAL_STATUS_COLORS[String(status || "").toLowerCase()] || {
    bg: "#ECEFF1",
    color: "#546E7A",
  };

/** Provider statuses are camelCase tokens ("partialReceived"); shown as words. */
const humanizeStatus = (s) =>
  String(s || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase()) || "—";

const StatTile = ({ label, value, hint, colors, emphasis }) => (
  <Box
    sx={{
      backgroundColor: colors?.bgSecondary || "#F5F7FA",
      border: `1px solid ${colors?.border || "#E8ECF0"}`,
      borderRadius: 1.5,
      px: 1.5,
      py: 1.25,
      height: "100%",
    }}
  >
    <Typography
      variant="caption"
      sx={{
        color: colors?.textTertiary || "var(--text-secondary)",
        fontWeight: 500,
        display: "block",
        mb: 0.25,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: 18,
        fontWeight: 600,
        lineHeight: 1.2,
        color: emphasis || colors?.textPrimary || "var(--text-heading)",
      }}
    >
      {value}
    </Typography>
    {hint ? (
      <Typography
        variant="caption"
        sx={{ color: colors?.textTertiary || "var(--text-secondary)" }}
      >
        {hint}
      </Typography>
    ) : null}
  </Box>
);

const HeadCell = ({ children, align, colors }) => (
  <TableCell
    align={align}
    sx={{
      py: 0.75,
      fontSize: 11,
      fontWeight: 600,
      color: colors?.textTertiary || "var(--text-secondary)",
      borderColor: colors?.border || "#E8ECF0",
    }}
  >
    {children}
  </TableCell>
);

const BodyCell = ({ children, align, colors, bold }) => (
  <TableCell
    align={align}
    sx={{
      py: 0.75,
      fontSize: 12,
      fontWeight: bold ? 600 : 400,
      color: colors?.textSecondary || "var(--text-tertiary)",
      borderColor: colors?.border || "#E8ECF0",
    }}
  >
    {children}
  </TableCell>
);

/** A field that only renders when the provider actually sent it — an empty row
 *  reads as "we know this is blank", which is a different claim. */
const Meta = ({ label, value, colors }) =>
  value ? (
    <Typography
      variant="caption"
      sx={{ color: colors?.textTertiary || "var(--text-secondary)" }}
    >
      {label}: <span style={{ fontWeight: 500 }}>{value}</span>
    </Typography>
  ) : null;

/** Serials arrive either as bare values or as inclusive "FROM..TO" ranges, and
 *  are stored exactly as the provider sends them (a range of 100 units is one
 *  fact, not 100 rows). Rendered as a single cell for the same reason. */
const serialText = (l) =>
  Array.isArray(l?.serial_numbers) && l.serial_numbers.length
    ? l.serial_numbers.join(", ")
    : null;

/** Whether ANY line on this set carries serials. The column is added only then,
 *  so the common no-serial case keeps the narrow four-column table. */
const anySerials = (lines) => (lines || []).some((l) => serialText(l));

const ReceiptCard = ({ receipt, colors }) => (
  <Box
    sx={{
      border: `1px solid ${colors?.border || "#E8ECF0"}`,
      borderLeft: "3px solid #2E7D32",
      borderRadius: 1.5,
      backgroundColor: colors?.bgPaper || "#fff",
      p: 1.5,
    }}
  >
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      flexWrap="wrap"
      sx={{ mb: 1, gap: 1 }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
        Receipt {receipt.ext_receipt_id || receipt.receipt_id || "—"}
      </Typography>
      <Meta
        label="Received on"
        // The only real goods-in timestamp in the chain — neither the arrival
        // nor the PO carries one.
        value={
          receipt.actual_arrival_date
            ? formatDateTime(receipt.actual_arrival_date)
            : null
        }
        colors={colors}
      />
    </Stack>
    <Table size="small">
      <TableHead>
        <TableRow>
          <HeadCell colors={colors}>SKU</HeadCell>
          <HeadCell colors={colors}>Lot</HeadCell>
          <HeadCell align="right" colors={colors}>
            Received
          </HeadCell>
          <HeadCell align="right" colors={colors}>
            Damaged
          </HeadCell>
          {anySerials(receipt.line_items) && (
            <HeadCell colors={colors}>Serials</HeadCell>
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        {(receipt.line_items || []).map((l, i) => (
          <TableRow key={`${l.sku}-${l.unique_id || i}`}>
            <BodyCell colors={colors}>{l.sku || "—"}</BodyCell>
            <BodyCell colors={colors}>{l.lot_number || "—"}</BodyCell>
            <BodyCell align="right" colors={colors} bold>
              {l.quantity ?? "—"}
            </BodyCell>
            {/* Damaged is a SUBSET of received, never netted off it. */}
            <BodyCell align="right" colors={colors}>
              {l.quantity_damaged ? (
                <span style={{ color: "#C62828", fontWeight: 600 }}>
                  {l.quantity_damaged}
                </span>
              ) : (
                "—"
              )}
            </BodyCell>
            {anySerials(receipt.line_items) && (
              <BodyCell colors={colors}>{serialText(l) || "—"}</BodyCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

const ArrivalCard = ({ arrival, colors }) => {
  const chip = statusChipSx(arrival.status);
  const receipts = arrival.receipts || [];
  return (
    <Box
      sx={{
        border: `1px solid ${colors?.border || "#E8ECF0"}`,
        borderRadius: 2,
        backgroundColor: colors?.bgSecondary || "#F5F7FA",
        p: 2,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        sx={{ gap: 1, mb: 1 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
            Arrival {arrival.ext_arrival_id || arrival.arrival_id || "—"}
          </Typography>
          <Chip
            label={humanizeStatus(arrival.status)}
            size="small"
            sx={{
              backgroundColor: chip.bg,
              color: chip.color,
              fontWeight: 600,
              fontSize: 11,
            }}
          />
        </Stack>
        <Meta
          label="Expected"
          value={
            arrival.expected_arrival_date
              ? formatDate(arrival.expected_arrival_date)
              : null
          }
          colors={colors}
        />
      </Stack>

      <Stack direction="row" flexWrap="wrap" sx={{ gap: 2, mb: 1.5 }}>
        <Meta label="Sender" value={arrival.sender_name} colors={colors} />
        <Meta label="Carrier" value={arrival.shipping_carrier} colors={colors} />
        <Meta label="Tracking" value={arrival.tracking_number} colors={colors} />
        <Meta label="BOL" value={arrival.bill_of_lading} colors={colors} />
        <Meta
          label="Container"
          value={arrival.container_number}
          colors={colors}
        />
        <Meta label="To location" value={arrival.to_location_id} colors={colors} />
        {/* Provider-optional transport detail. `Meta` renders nothing for a
            falsy value, so a delivery that reports none of this adds no empty
            rows — which is the normal case for a parcel and the exception for
            a container. */}
        <Meta label="Vendor status" value={arrival.ext_status} colors={colors} />
        <Meta label="Method" value={arrival.shipping_method} colors={colors} />
        <Meta label="Incoterms" value={arrival.incoterms} colors={colors} />
        <Meta
          label="Container size/type"
          value={
            [arrival.container_size, arrival.container_type]
              .filter(Boolean)
              .join(" / ") || null
          }
          colors={colors}
        />
        <Meta label="Seal" value={arrival.seal} colors={colors} />
        <Meta
          label="Trailer"
          value={
            [
              arrival.trailer_number,
              [arrival.trailer_size, arrival.trailer_type]
                .filter(Boolean)
                .join(" "),
            ]
              .filter(Boolean)
              .join(" · ") || null
          }
          colors={colors}
        />
        <Meta
          label="Weight"
          value={
            arrival.total_weight
              ? `${arrival.total_weight}${
                  arrival.weight_unit ? ` ${arrival.weight_unit}` : ""
                }`
              : null
          }
          colors={colors}
        />
        <Meta label="Note" value={arrival.shipping_note} colors={colors} />
      </Stack>

      <Table size="small">
        <TableHead>
          <TableRow>
            <HeadCell colors={colors}>SKU</HeadCell>
            <HeadCell align="right" colors={colors}>
              Expected
            </HeadCell>
            <HeadCell align="right" colors={colors}>
              Arrived
            </HeadCell>
            <HeadCell colors={colors}>Lot</HeadCell>
            <HeadCell colors={colors}>Expires</HeadCell>
            {anySerials(arrival.line_items) && (
              <HeadCell colors={colors}>Serials</HeadCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {(arrival.line_items || []).map((l, i) => (
            <TableRow key={`${l.sku}-${l.unique_id || i}`}>
              <BodyCell colors={colors}>
                {l.sku || "—"}
                {l.name && l.name !== l.sku ? ` | ${l.name}` : ""}
              </BodyCell>
              <BodyCell align="right" colors={colors}>
                {l.quantity_expected ?? "—"}
              </BodyCell>
              <BodyCell align="right" colors={colors} bold>
                {arrivedQty(l, arrival.status)}
              </BodyCell>
              <BodyCell colors={colors}>{l.lot_number || "—"}</BodyCell>
              <BodyCell colors={colors}>
                {l.expiration_date ? formatDate(l.expiration_date) : "—"}
              </BodyCell>
              {anySerials(arrival.line_items) && (
                <BodyCell colors={colors}>{serialText(l) || "—"}</BodyCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Divider sx={{ my: 1.5, borderColor: colors?.border || "#E8ECF0" }} />

      {receipts.length ? (
        <Stack spacing={1.5}>
          {receipts.map((r, i) => (
            <ReceiptCard key={r.receipt_id || i} receipt={r} colors={colors} />
          ))}
        </Stack>
      ) : (
        // An arrival with no receipts is the dock case, and saying so plainly
        // is the whole value of separating arrived from received.
        <Typography
          variant="caption"
          sx={{ color: colors?.textTertiary || "var(--text-secondary)" }}
        >
          Arrived but not yet booked in — no goods-in receipt recorded against
          this delivery.
        </Typography>
      )}
    </Box>
  );
};

const POArrivalsView = ({ asn, colors }) => {
  const arrivals = asn?.arrivals || [];
  const totals = receivingTotals(asn);
  const bySku = receivingBySku(asn);
  const lastReceived = lastReceivedDate(asn);

  if (!arrivals.length) {
    return (
      <Typography
        variant="body2"
        sx={{ color: colors?.textTertiary || "var(--text-secondary)" }}
      >
        Nothing has arrived against this PO yet. Deliveries and their goods-in
        receipts appear here as the supplier ships and the warehouse books them
        in.
      </Typography>
    );
  }

  return (
    <Box>
      {/* Summary */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={4} md={2}>
          <StatTile label="Ordered" value={totals.ordered} colors={colors} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatTile
            label="Arrived"
            value={totals.arrived}
            hint="at the door"
            colors={colors}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatTile
            label="Received"
            value={totals.received}
            hint={
              totals.receivedPct === null
                ? "booked in"
                : `${totals.receivedPct}% booked in`
            }
            colors={colors}
            emphasis="#2E7D32"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatTile
            label="Outstanding"
            value={totals.outstanding}
            colors={colors}
            emphasis={totals.outstanding > 0 ? "#E65100" : undefined}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatTile
            label="Damaged"
            value={totals.damaged}
            hint="of received"
            colors={colors}
            emphasis={totals.damaged > 0 ? "#C62828" : undefined}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatTile
            label="Deliveries"
            value={`${totals.arrivals} / ${totals.receipts}`}
            hint="arrivals / receipts"
            colors={colors}
          />
        </Grid>
      </Grid>

      {lastReceived ? (
        <Typography
          variant="caption"
          sx={{
            color: colors?.textTertiary || "var(--text-secondary)",
            display: "block",
            mb: 2,
          }}
        >
          Last booked in {formatDateTime(lastReceived)}
        </Typography>
      ) : null}

      {/* Per-SKU roll-up */}
      <CustomTypography
        color={colors?.textPrimary || "var(--text-heading)"}
        fontSize="13px"
        fontWeight={600}
      >
        By SKU
      </CustomTypography>
      <Box
        sx={{
          border: `1px solid ${colors?.border || "#E8ECF0"}`,
          borderRadius: 1.5,
          mt: 1,
          mb: 2.5,
          overflowX: "auto",
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <HeadCell colors={colors}>SKU</HeadCell>
              <HeadCell align="right" colors={colors}>
                Ordered
              </HeadCell>
              <HeadCell align="right" colors={colors}>
                Arrived
              </HeadCell>
              <HeadCell align="right" colors={colors}>
                Received
              </HeadCell>
              <HeadCell align="right" colors={colors}>
                Damaged
              </HeadCell>
              <HeadCell align="right" colors={colors}>
                Outstanding
              </HeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bySku.map((r) => {
              const outstanding = Math.max(r.ordered - r.received, 0);
              return (
                <TableRow key={r.sku}>
                  <BodyCell colors={colors}>{r.sku}</BodyCell>
                  <BodyCell align="right" colors={colors}>
                    {r.ordered}
                  </BodyCell>
                  <BodyCell align="right" colors={colors}>
                    {r.arrived}
                  </BodyCell>
                  <BodyCell align="right" colors={colors} bold>
                    {r.received}
                  </BodyCell>
                  <BodyCell align="right" colors={colors}>
                    {r.damaged ? (
                      <span style={{ color: "#C62828", fontWeight: 600 }}>
                        {r.damaged}
                      </span>
                    ) : (
                      "—"
                    )}
                  </BodyCell>
                  <BodyCell align="right" colors={colors}>
                    {outstanding ? (
                      <span style={{ color: "#E65100", fontWeight: 600 }}>
                        {outstanding}
                      </span>
                    ) : (
                      "—"
                    )}
                  </BodyCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      {/* Per-delivery detail */}
      <CustomTypography
        color={colors?.textPrimary || "var(--text-heading)"}
        fontSize="13px"
        fontWeight={600}
      >
        Deliveries ({arrivals.length})
      </CustomTypography>
      <Stack spacing={2} sx={{ mt: 1 }}>
        {arrivals.map((a, i) => (
          <ArrivalCard key={a.arrival_id || i} arrival={a} colors={colors} />
        ))}
      </Stack>
    </Box>
  );
};

export default POArrivalsView;
